import { useTranslation } from 'hooks';
import {
  IconAddress,
  IconBed,
  IconBuilding,
  IconBuildingWarehouse,
  IconCompass,
  IconConstruction,
  IconDollarOutline,
  IconGitBranch,
  IconHouseRear,
  IconInvestor,
  IconLandRows,
  IconLocationOutline,
  IconNewLine,
  IconNote,
  IconNote2,
  IconOfficeBuildingOutline,
  IconPanHorizontal,
  IconPanVertical,
  IconProjectOutline,
  IconSettingServices,
  IconShower,
  IconStairs,
  IconStar,
  IconVectorSquareClose,
  IconWrap,
} from 'components/icons';
import { includes, isEqual, isNil, map, toNumber, toString } from 'lodash';
import { DetailPageContext } from 'pages/[ket-qua-tim-kiem]/[chi-tiet-tin-rao]';
import { useContext } from 'react';
import { convertNumberToVietnameseCurrencyShortString } from 'utils';

const fieldForRealEstateType = {
  townhouse: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'industryTrade',
    'pageNo',
    'parcel',
  ],
  villa: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'project',
    'pageNo',
    'parcel',
  ],
  building: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'buildingType',
    'pageNo',
    'parcel',
  ],
  hotel: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'hotelStar',
    'pageNo',
    'parcel',
  ],
  warehouse: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'warehouseType',
    'pageNo',
    'parcel',
  ],
  land: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'direction',
    'project',
    'block',
    'investor',
    'warehouseType',
    'pageNo',
    'parcel',
  ],
  apartment: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'onFloor',
    'clearanceArea',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'project',
    'block',
    'investor',
    'pageNo',
    'parcel',
  ],
  shophouse: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'project',
    'block',
    'investor',
    'industryTrade',
    'pageNo',
    'parcel',
  ],
  penthouse: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'onFloor',
    'clearanceArea',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction,',
    'project',
    'block',
    'investor',
    'pageNo',
    'parcel',
  ],
  condotel: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'onFloor',
    'clearanceArea',
    'constructionArea',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'project',
    'block',
    'investor',
    'pageNo',
    'parcel',
  ],
  motel: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'rear',
    'area',
    'price',
    'floorCount',
    'onFloor',
    'bedroomCount',
    'toiletCount',
    'direction',
    'isNewConstruction',
    'hasMezzanine',
    'pageNo',
    'parcel',
  ],
  house: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'bedroomCount',
    'toiletCount',
    'rear',
    'direction',
    'area',
    'isNewConstruction',
    'price',
    'industryTrade',
    'floorCount',
    'constructionArea',
    'pageNo',
    'parcel',
  ],
  farm: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'bedroomCount',
    'toiletCount',
    'rear',
    'direction',
    'area',
    'isNewConstruction',
    'price',
    'farmLandUse',
    'floorCount',
    'constructionArea',
    'pageNo',
    'parcel',
  ],
  other: [
    'type',
    'fullAddress',
    'hasAlley',
    'alleyTurnCount',
    'frontHomeAlleyWidth',
    'width',
    'length',
    'bedroomCount',
    'toiletCount',
    'rear',
    'direction',
    'area',
    'isNewConstruction',
    'price',
    'industryTrade',
    'floorCount',
    'constructionArea',
    'pageNo',
    'parcel',
  ],
};

const SaleInformation = () => {
  const { realEstate } = useContext(DetailPageContext).pageData ?? {};
  const {
    isForSell,
    type,
    fullAddress,
    hasAlley,
    alleyTurnCount,
    frontHomeAlleyWidth,
    width,
    length,
    rear,
    area,
    price,
    floorCount,
    constructionArea,
    bedroomCount,
    toiletCount,
    direction,
    isNewConstruction,
    industryTrade,
    project,
    hotelStar,
    buildingType,
    warehouseType,
    block,
    onFloor,
    clearanceArea,
    investor,
    hasMezzanine,
    farmLandUse,
    pageNo,
    parcel,
  } = realEstate ?? {};
  const translation = useTranslation();
  const information = {
    type: {
      icon: <IconBuilding className='h-[20px] min-w-[20px]' />,
      content: isNil(isForSell)
        ? null
        : (isForSell
            ? translation.major.forSellRealEstateType
            : (translation.major.forRentRealEstateType as any))[toString(type)],
    },
    fullAddress: {
      icon: <IconAddress className='h-[20px] min-w-[20px]' />,
      content: fullAddress ? fullAddress : null,
    },
    hasAlley: {
      icon: <IconLocationOutline className='h-[20px] min-w-[20px]' />,
      content: isNil(hasAlley) ? null : (translation.major.hasAlley as any)[toString(hasAlley)],
    },
    alleyTurnCount: {
      icon: <IconWrap className='h-[20px] min-w-[20px]' />,
      content: isNil(alleyTurnCount) ? null : alleyTurnCount.toLocaleString(),
    },
    frontHomeAlleyWidth: {
      icon: <IconPanHorizontal className='h-[20px] min-w-[20px]' />,
      content: isNil(frontHomeAlleyWidth) ? null : frontHomeAlleyWidth.toLocaleString(),
    },
    width: {
      icon: <IconPanHorizontal className='h-[20px] min-w-[20px]' />,
      content: isNil(width) ? null : `${width.toLocaleString()} m`,
    },
    length: {
      icon: <IconPanVertical className='h-[20px] min-w-[20px]' />,
      content: isNil(length) ? null : `${length.toLocaleString()} m`,
    },
    rear: {
      icon: <IconHouseRear className='h-[20px] min-w-[20px]' />,
      content: isNil(rear) ? null : `${rear.toLocaleString()} m`,
    },
    area: {
      icon: <IconVectorSquareClose className='h-[20px] min-w-[20px]' />,
      content: isNil(area) ? null : `${area.toLocaleString()} m\u00B2`,
    },
    price: {
      icon: <IconDollarOutline className='h-[20px] min-w-[20px]' />,
      content:
        isNil(price) || isEqual(toNumber(price), 0)
          ? 'Thương lượng'
          : `${convertNumberToVietnameseCurrencyShortString(toNumber(price))}${
              isForSell ? '' : '/tháng'
            }`,
      extraClassName: 'text-alert font-[600]',
    },
    floorCount: {
      icon: <IconStairs className='h-[20px] min-w-[20px]' />,
      content: isNil(floorCount) ? null : floorCount.toLocaleString(),
    },
    constructionArea: {
      icon: <IconVectorSquareClose className='h-[20px] min-w-[20px]' />,
      content: isNil(constructionArea) ? null : `${constructionArea.toLocaleString()} m\u00B2`,
    },
    bedroomCount: {
      icon: <IconBed className='h-[20px] min-w-[20px]' />,
      content: isNil(bedroomCount) ? null : bedroomCount.toLocaleString(),
    },
    toiletCount: {
      icon: <IconShower className='h-[20px] min-w-[20px]' />,
      content: isNil(toiletCount) ? null : toiletCount.toLocaleString(),
    },
    direction: {
      icon: <IconCompass className='h-[20px] min-w-[20px]' />,
      content: isNil(direction) ? null : (translation.major.direction as any)[direction],
    },
    isNewConstruction: {
      icon: <IconNewLine className='h-[20px] min-w-[20px]' />,
      content: isNil(isNewConstruction)
        ? null
        : (translation.major.isNewConstruction as any)[toString(isNewConstruction)],
    },
    industryTrade: {
      icon: <IconGitBranch className='h-[20px] min-w-[20px]' />,
      content: industryTrade ? industryTrade : null,
    },
    project: {
      icon: <IconProjectOutline className='h-[20px] min-w-[20px]' />,
      content: isNil(project) ? null : project.name,
    },
    hotelStar: {
      icon: <IconStar className='h-[20px] min-w-[20px]' />,
      content: isNil(hotelStar) ? null : hotelStar.toLocaleString(),
    },
    buildingType: {
      icon: <IconBuilding className='h-[20px] min-w-[20px]' />,
      content: isNil(buildingType) ? null : (translation.major.buildingType as any)[buildingType],
    },
    warehouseType: {
      icon: <IconBuildingWarehouse className='h-[20px] min-w-[20px]' />,
      content: isNil(warehouseType)
        ? null
        : (translation.major.warehouseType as any)[warehouseType],
    },
    farmLandUse: {
      icon: <IconSettingServices className='h-[20px] min-w-[20px]' />,
      content: isNil(farmLandUse)
        ? null
        : (translation.major.farmLandUse as any)[farmLandUse as string],
    },
    block: {
      icon: <IconOfficeBuildingOutline className='h-[20px] min-w-[20px]' />,
      content: block ? block : null,
    },
    onFloor: {
      icon: <IconStairs className='h-[20px] min-w-[20px]' />,
      content: isNil(onFloor) ? null : onFloor.toLocaleString(),
    },
    clearanceArea: {
      icon: <IconVectorSquareClose className='h-[20px] min-w-[20px]' />,
      content: isNil(clearanceArea) ? null : `${clearanceArea.toLocaleString()} m\u00B2`,
    },
    investor: {
      icon: <IconInvestor className='h-[20px] min-w-[20px]' />,
      content: investor ? investor : null,
    },
    hasMezzanine: {
      icon: <IconConstruction className='h-[20px] min-w-[20px]' />,
      content: isNil(hasMezzanine)
        ? null
        : (translation.major.hasMezzanine as any)[toString(hasMezzanine)],
    },
    pageNo: {
      icon: <IconNote2 className='h-[20px] min-w-[20px]' />,
      content: isNil(pageNo) ? null : pageNo.toLocaleString(),
    },
    parcel: {
      icon: <IconLandRows className='h-[20px] min-w-[20px]' />,
      content: isNil(parcel) ? null : parcel.toLocaleString(),
    },
  };

  return (
    <div id='sale-information' className='flex flex-col space-y-[16px]'>
      <div className='flex items-center space-x-[10px]'>
        <div className='flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-background'>
          <IconNote className='h-[18px] w-[18px]' />
        </div>
        <h2 className='text-[18px] font-[600] leading-[23px]'>
          Thông tin {isForSell ? 'bán' : 'cho thuê'}
        </h2>
      </div>
      <div className='flex flex-col items-center'>
        <div className='flex flex-wrap'>
          {map(
            (fieldForRealEstateType as any)[toString(type)],
            (field, fieldIndex) =>
              (information as any)[field]?.content && (
                <div key={fieldIndex} className='min-h-[64px] w-1/2'>
                  <div className='flex h-full space-x-[12px] border-b border-stroke py-[16px]'>
                    <div className='flex min-w-[150px] max-w-[150px] space-x-[10px] xl:min-w-[112px] xl:max-w-[112px]'>
                      {(information as any)[field].icon}
                      <span className='w-full font-[600]'>
                        {(translation.desktop.detail as any)[field]}
                        {includes(['apartment', 'penthouse', 'condotel'], type) &&
                        isEqual(field, 'area')
                          ? ' tim tường'
                          : ''}
                      </span>
                    </div>
                    <span
                      className={
                        (information as any)[field].extraClassName
                          ? (information as any)[field].extraClassName
                          : ''
                      }
                    >
                      {(information as any)[field].content}
                    </span>
                  </div>
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
};

export default SaleInformation;
